import { useCallback, useEffect, useRef, useState } from "react";
import { METRIC } from "./supernovaSummary";
import { automatedStrategiesFromStatus } from "./supernovaSlots";

/**
 * useAutomationSlots — which strategies are currently consuming slots.
 *
 * The Supernova tab owns the STAKE half of the slot rule (chain reads). This
 * hook supplies the other half, and only the other half: the automation
 * engine's saved pair allowlist, read through the existing GET /status
 * endpoint. Read-only, display-only. It changes no config, places no order, and
 * touches nothing on a money path.
 *
 * WHY THE DYNAMIC IMPORT: services/signalsApi.js is HUMAN-ONLY and pulls in
 * Firebase auth at module scope. Importing it statically would drag that into
 * every render of a tab that, in the shipping default (backend URL unset), is
 * never going to call it. So the module is loaded only on the path that
 * actually makes the request, and the environment check that decides that is a
 * plain env read — the same condition signalsApi.isConfigured() applies.
 *
 * STATUS, NEVER A NUMBER WE DON'T HAVE (mirrors useSupernovaChain):
 *   "unavailable"  the automation backend isn't configured in this environment
 *                  ⇒ labelled placeholder, NOT "0 slots in use"
 *   "loading"      request in flight
 *   "ready"        `strategies` is the engine's own list. A 404 lands here with
 *                  an empty list: the engine answered, and its answer is that
 *                  this member has no automation record — genuinely nothing
 *                  automated, which is a fact, not a gap.
 *   "error"        the read failed ⇒ say so; never fall back to zero
 *
 * NOT AN ENTITLEMENT. This is the member's own automation config, keyed off
 * their own account. Nothing here reads, writes, or infers a membership record,
 * and stake state is never derived from it.
 *
 * @param {Object} [options]
 * @param {boolean} [options.enabled=true]  false ⇒ no request of any kind
 * @param {Object} [options.service]        injectable signalsApi (tests). Pass a
 *        stable reference — it is a dependency of the read effect.
 */

export const AUTOMATION_UNAVAILABLE_NOTE =
  "Slot usage comes from Strategy Automation, which isn't connected in this environment.";
export const AUTOMATION_ERROR_NOTE =
  "We couldn't read which strategies are using your slots. Your stake is unaffected.";

/** Same condition as signalsApi.isConfigured(), without importing the module. */
export function isAutomationBackendConfigured() {
  const base = process.env.REACT_APP_SIGNALS_API_URL || "";
  return Boolean(base && base.startsWith("http"));
}

export default function useAutomationSlots(options = {}) {
  const { enabled = true, service } = options;
  const available = Boolean(enabled && (service || isAutomationBackendConfigured()));

  const [strategies, setStrategies] = useState(null);
  const [status, setStatus] = useState(METRIC.UNAVAILABLE);
  const [message, setMessage] = useState(null);

  /* Guards a resolved read from overwriting newer state after a refresh or an
     unmount — same pattern as useSupernovaChain. */
  const requestRef = useRef(0);

  const refresh = useCallback(async () => {
    if (!available) {
      setStrategies(null);
      setStatus(METRIC.UNAVAILABLE);
      setMessage(null);
      return;
    }

    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    const current = () => requestRef.current === requestId;

    setStatus(METRIC.LOADING);
    try {
      const api = service || (await import("../../services/signalsApi")).default;
      const payload = await api.getStatus();
      if (!current()) return;
      const list = automatedStrategiesFromStatus(payload);
      if (list === null) {
        /* The engine answered but carried no allowlist — we know nothing about
           usage, so we say nothing about it. */
        setStrategies(null);
        setStatus(METRIC.UNAVAILABLE);
        setMessage(null);
        return;
      }
      setStrategies(list);
      setStatus(METRIC.READY);
      setMessage(null);
    } catch (e) {
      if (!current()) return;
      /* 404 before enrollment is the engine saying "no record", the same
         convention the Strategy Automation tab uses. No automation record ⇒ no
         strategy is consuming a slot. */
      if (e?.code === "HTTP_404") {
        setStrategies([]);
        setStatus(METRIC.READY);
        setMessage(null);
        return;
      }
      setStrategies(null);
      setStatus(METRIC.ERROR);
      setMessage(AUTOMATION_ERROR_NOTE);
    }
  }, [available, service]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const used = Array.isArray(strategies) ? strategies.length : null;

  return {
    status,
    strategies,
    used,
    message,
    note: status === METRIC.UNAVAILABLE ? AUTOMATION_UNAVAILABLE_NOTE : null,
    refresh,
  };
}
